import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQS = [
  {
    q: "Do I need to provide water or power?",
    a: "Yes — you provide the water and power. We'll need access to a water spigot and a power outlet at your location. We bring all the equipment and professional-grade products, hook up on site, and handle the rest.",
  },
  {
    q: "How long does a detail take?",
    a: "A Basic Clean typically takes 1 to 1.5 hours. A Full Detail runs 2 to 3 hours, and a Disaster Detail can take 3 to 5 hours depending on the condition. We'll give you a time estimate when you book.",
  },
  {
    q: "What makes the Disaster Detail different?",
    a: "It's built for vehicles in rough shape — embedded dog hair, set-in stains, heavy mud or grime. We use extraction equipment and odor neutralization to bring it back. It's very thorough by design.",
  },
  {
    q: "How is the final price decided?",
    a: "Each package has a range based on vehicle size and condition. A compact sedan in decent shape lands at the low end; a large SUV needing heavy work lands higher. Text us a photo for an exact quote before we start.",
  },
  {
    q: "How do I pay?",
    a: "Pay after the detail is complete and you've inspected the vehicle. We accept cash, card, and popular payment apps — details confirmed when you book.",
  },
];

export default function FAQSection() {
  return (
    <section id="faq" data-testid="faq-section" className="relative py-24 sm:py-36 bg-[#0e1117]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.4fr] gap-14 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400" data-testid="faq-eyebrow">
            04 — Questions
          </span>
          <h2 className="mt-4 font-syne font-extrabold uppercase tracking-tight leading-none text-4xl sm:text-6xl text-white">
            Good To <span className="text-amber-400">Know</span>
          </h2>
          <div
            data-testid="service-area-card"
            className="mt-10 rounded-2xl border border-white/10 bg-[#12161F] p-7"
          >
            <div className="flex items-center gap-3">
              <MapPin size={20} className="text-amber-400" />
              <h3 className="font-syne font-bold text-lg uppercase text-white">Service Area</h3>
            </div>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">
              Based in Benson, NC — we serve Benson, Clayton, and surrounding
              Johnston County communities. Not sure if you're in range? Text us your
              address and we'll confirm.
            </p>
            <a
              href="sms:9197373526?&body=Hi%20P%26H%20Detailing!%20Is%20my%20address%20in%20your%20service%20area%3F"
              data-testid="service-area-text-button"
              className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-amber-400 hover:text-amber-300 transition-colors duration-300"
            >
              Text Us Your Address
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-white/10"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="text-left font-syne font-bold text-base sm:text-lg text-slate-100 hover:text-amber-400 hover:no-underline py-6 transition-colors duration-300"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`faq-content-${i}`}
                  className="text-slate-400 text-sm sm:text-base leading-relaxed pb-6"
                >
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
